import { Table as TanStackTable } from '@tanstack/react-table';
import { Box, TablePagination as MuiTablePagination } from '@mui/material';
import { TABLE_PAGINATION } from '@/constants';
import TablePaginationActions from './TablePaginationActions';

interface TablePaginationProps<TData extends object> {
    table: TanStackTable<TData>;
    totalRows: number;
    rowsPerPageOptions?: number[];
}

/**
 * Renders the pagination component.
 *
 * @template TData
 * @param {TablePaginationProps<TData>} props
 * @returns {JSX.Element}
 */
const TablePagination = <TData extends object>({
    table,
    totalRows,
    rowsPerPageOptions = TABLE_PAGINATION.ROWS_PER_PAGE_OPTIONS,
}: TablePaginationProps<TData>) => {
    const pagination = table.getState().pagination;
    const maxPage = Math.max(0, Math.ceil(totalRows / pagination.pageSize) - 1);
    const currentPage = pagination.pageIndex;
    const safePage = Math.min(currentPage, maxPage);

    const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        table.setPageIndex(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        table.setPageSize(parseInt(event.target.value, 10));
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1 }}>
            <MuiTablePagination
                sx={{ width: '100%', overflow: 'hidden' }}
                component="div"
                count={totalRows}
                page={safePage}
                rowsPerPage={pagination.pageSize}
                rowsPerPageOptions={rowsPerPageOptions}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                showFirstButton
                showLastButton
                labelRowsPerPage="Rows per page"
                labelDisplayedRows={({ from, to, count }) => `Displaying ${from} - ${to} of ${count !== -1 ? count : `more than ${to}`}`}
            />
        </Box>
    );
};

export default TablePagination;
